import { TICK_MS } from "./constants";

/** Power-up pods that can appear on the map (P8.6/P8.7). */
export type PowerUpKind =
	| "tripleLaser"
	| "warp"
	| "shield"
	| "freeze"
	| "extraMissiles"
	| "homing"
	| "fuel"
	/** Restores hull points (hull damage rule, P11.11). */
	| "repair";

export const POWER_UP_KINDS: readonly PowerUpKind[] = [
	"tripleLaser",
	"warp",
	"shield",
	"freeze",
	"extraMissiles",
	"homing",
	"fuel",
	"repair",
];

/**
 * Optional gameplay rules layered on top of the 2008 behaviour. Stored in the world state
 * (they change the simulation, so they are part of determinism and replays). Times are in
 * ticks, distances in world units; `null` selects the legacy behaviour where noted.
 */
export interface MatchRules {
	/** Gravity and collisions work across the world seam (legacy ignored it). */
	wrapPhysics: boolean;
	/** Thrust burns fuel; it slowly regenerates when coasting. */
	fuel: boolean;
	/** The map's fueling stations refill ships inside their range (needs the fuel rule, I-026). */
	fuelStations: boolean;
	/** Fuel burn while thrusting, as a multiple of the base rate (1/tick at ×1). Warp never burns fuel (I-025). */
	fuelBurnRate: number;
	/** Fuel regen while coasting, as a multiple of the base rate (0.25/tick at ×1). */
	fuelRegenRate: number;
	/** Ships and asteroids have hit points instead of dying to one hit. */
	damage: boolean;
	/** Lasers reflect off planets instead of vanishing. */
	laserBounce: boolean;
	/** A laser splits an asteroid into two smaller ones (full → half → quarter, P11.6). */
	asteroidSplitting: boolean;
	/**
	 * New asteroids appear at random spots clear of ships, spawn points and other objects
	 * (I-027); off = legacy, they appear at the map's spawn points.
	 */
	safeAsteroidSpawns: boolean;

	/** Each laser shot heats the gun; at max heat it locks until fully cooled. */
	laserOverheat: boolean;
	laserHeatMax: number;
	laserHeatPerShot: number;
	/** Ticks to cool from max heat to cold. */
	laserCooldownTicks: number;
	/** Distance a laser flies; null = legacy 40-tick lifetime. */
	laserRange: number | null;

	/** Missiles each new ship carries; null = unlimited (legacy). */
	missilesPerLife: number | null;
	/** Homing missiles turn at most this many degrees per tick. */
	homingTurnDeg: number;
	/** Ticks a homing missile steers before flying on ballistically. */
	homingLockTicks: number;

	/** Number of random spawn points replacing the map's; null = the map's own. */
	spawnPoints: number | null;

	/**
	 * Growing planets (P11.7, 2008 req. 4.1.21): mass a planet gains per asteroid crashing into
	 * it, up to twice its original mass. null = off (fixed mass).
	 */
	planetGrowth: number | null;

	/**
	 * Duels (P11.4, 2008 req. 4.1.5): first to this score wins, with unlimited ships; +1 for
	 * destroying an opponent, −1 for crashing (planet, asteroid, own shot). null = lives.
	 */
	scoreLimit: number | null;

	/** Power-up pods appear around the map. */
	powerUps: boolean;
	powerUpIntervalMinTicks: number;
	powerUpIntervalMaxTicks: number;
	/** Most pods on the map at once. */
	powerUpMax: number;
	/** Ticks an uncollected pod stays on the map. */
	powerUpLifetimeTicks: number;
	/** Ticks a timed power-up (triple laser, warp) lasts. */
	powerUpDurationTicks: number;
	powerUpTypes: Record<PowerUpKind, boolean>;

	// Per power-up tuning.
	/** null = powerUpDurationTicks. */
	tripleLaserDurationTicks: number | null;
	tripleLaserSpreadDeg: number;
	/** null = powerUpDurationTicks. */
	warpDurationTicks: number | null;
	warpThrustFactor: number;
	warpSpeedFactor: number;
	/** Laser hits a shield absorbs (a missile, asteroid or planet always uses it all). */
	shieldHits: number;
	freezeTicks: number;
	extraMissiles: number;
	homingShots: number;
	fuelRefill: number;
	/** Hull points a repair pod restores (up to a full hull). */
	repairHull: number;
}

type KeysOfType<T, V> = { [K in keyof T]-?: T[K] extends V ? K : never }[keyof T];
export type BooleanRuleKey = KeysOfType<MatchRules, boolean>;
export type NumberRuleKey = KeysOfType<MatchRules, number | null>;
/** Kept for the summary/toggle UIs: the on/off rules. */
export type RuleKey = BooleanRuleKey;

export const BOOLEAN_RULE_KEYS: readonly BooleanRuleKey[] = [
	"wrapPhysics",
	"fuel",
	"fuelStations",
	"damage",
	"laserBounce",
	"asteroidSplitting",
	"safeAsteroidSpawns",
	"laserOverheat",
	"powerUps",
];
export const RULE_KEYS = BOOLEAN_RULE_KEYS;

export interface NumberSpec {
	min: number;
	max: number;
	/** Values are rounded to a multiple of this. */
	step: number;
	/** How the value is shown to players (the sim always uses the raw value). */
	unit: "ticks" | "units" | "count" | "heat" | "deg" | "factor";
	/** null is allowed (legacy / unlimited / map / same-as-global, see MatchRules). */
	nullable?: boolean;
}

/** Converts seconds to whole ticks. */
export function secondsToTicks(seconds: number): number {
	return Math.round((seconds * 1000) / TICK_MS);
}

/** The longest laser range any map allows (2 × the longest side of the largest map). */
export const LASER_RANGE_LIMIT = 14_000;

/** Ranges and steps of every numeric rule: validation and the options UI both use it. */
export const RULE_SPECS: Record<NumberRuleKey, NumberSpec> = {
	fuelBurnRate: { min: 0.25, max: 4, step: 0.25, unit: "factor" },
	fuelRegenRate: { min: 0.25, max: 4, step: 0.25, unit: "factor" },
	laserHeatMax: { min: 20, max: 500, step: 1, unit: "heat" },
	laserHeatPerShot: { min: 0.5, max: 50, step: 0.5, unit: "heat" },
	laserCooldownTicks: { min: secondsToTicks(0.5), max: secondsToTicks(20), step: 1, unit: "ticks" },
	laserRange: { min: 200, max: LASER_RANGE_LIMIT, step: 50, unit: "units", nullable: true },
	missilesPerLife: { min: 0, max: 50, step: 1, unit: "count", nullable: true },
	homingTurnDeg: { min: 0.3, max: 8, step: 0.1, unit: "deg" },
	homingLockTicks: { min: secondsToTicks(1), max: secondsToTicks(15), step: 1, unit: "ticks" },
	spawnPoints: { min: 1, max: 10, step: 1, unit: "count", nullable: true },
	scoreLimit: { min: 1, max: 50, step: 1, unit: "count", nullable: true },
	planetGrowth: { min: 5, max: 200, step: 5, unit: "count", nullable: true },
	powerUpIntervalMinTicks: {
		min: secondsToTicks(1),
		max: secondsToTicks(120),
		step: 1,
		unit: "ticks",
	},
	powerUpIntervalMaxTicks: {
		min: secondsToTicks(1),
		max: secondsToTicks(120),
		step: 1,
		unit: "ticks",
	},
	powerUpMax: { min: 1, max: 10, step: 1, unit: "count" },
	powerUpLifetimeTicks: {
		min: secondsToTicks(5),
		max: secondsToTicks(120),
		step: 1,
		unit: "ticks",
	},
	powerUpDurationTicks: { min: secondsToTicks(1), max: secondsToTicks(60), step: 1, unit: "ticks" },
	tripleLaserDurationTicks: {
		min: secondsToTicks(1),
		max: secondsToTicks(60),
		step: 1,
		unit: "ticks",
		nullable: true,
	},
	tripleLaserSpreadDeg: { min: 5, max: 60, step: 1, unit: "deg" },
	warpDurationTicks: {
		min: secondsToTicks(1),
		max: secondsToTicks(60),
		step: 1,
		unit: "ticks",
		nullable: true,
	},
	warpThrustFactor: { min: 1.2, max: 4, step: 0.1, unit: "factor" },
	warpSpeedFactor: { min: 1, max: 3, step: 0.1, unit: "factor" },
	shieldHits: { min: 1, max: 10, step: 1, unit: "count" },
	freezeTicks: { min: secondsToTicks(0.5), max: secondsToTicks(10), step: 1, unit: "ticks" },
	extraMissiles: { min: 1, max: 50, step: 1, unit: "count" },
	homingShots: { min: 1, max: 20, step: 1, unit: "count" },
	fuelRefill: { min: 50, max: 600, step: 10, unit: "units" },
	repairHull: { min: 1, max: 5, step: 1, unit: "count" },
};

export const NUMBER_RULE_KEYS = Object.keys(RULE_SPECS) as NumberRuleKey[];

function allPowerUps(on: boolean): Record<PowerUpKind, boolean> {
	return Object.fromEntries(POWER_UP_KINDS.map((k) => [k, on])) as Record<PowerUpKind, boolean>;
}

/** Everything the original started but never finished, plus the Phase 8 mechanics, on. */
export const MODERN_RULES: Readonly<MatchRules> = Object.freeze({
	wrapPhysics: true,
	fuel: true,
	fuelStations: true,
	fuelBurnRate: 1,
	fuelRegenRate: 1,
	damage: true,
	laserBounce: true,
	asteroidSplitting: false,
	safeAsteroidSpawns: true,
	laserOverheat: true,
	laserHeatMax: 100,
	laserHeatPerShot: 2,
	laserCooldownTicks: secondsToTicks(3),
	laserRange: 800,
	missilesPerLife: 5,
	homingTurnDeg: 2,
	homingLockTicks: secondsToTicks(4),
	spawnPoints: null,
	scoreLimit: null,
	planetGrowth: null,
	powerUps: true,
	powerUpIntervalMinTicks: secondsToTicks(8),
	powerUpIntervalMaxTicks: secondsToTicks(15),
	powerUpMax: 3,
	powerUpLifetimeTicks: secondsToTicks(30),
	powerUpDurationTicks: secondsToTicks(10),
	powerUpTypes: Object.freeze(allPowerUps(true)),
	tripleLaserDurationTicks: null,
	tripleLaserSpreadDeg: 25,
	warpDurationTicks: null,
	warpThrustFactor: 2,
	warpSpeedFactor: 1.5,
	shieldHits: 3,
	freezeTicks: secondsToTicks(3),
	extraMissiles: 10,
	homingShots: 3,
	fuelRefill: 300,
	repairHull: 3,
});

/** Exactly the original game: every optional mechanic off, legacy lasers and missiles. */
export const CLASSIC_RULES: Readonly<MatchRules> = Object.freeze({
	...MODERN_RULES,
	wrapPhysics: false,
	fuel: false,
	fuelStations: false,
	damage: false,
	laserBounce: false,
	asteroidSplitting: false,
	safeAsteroidSpawns: false,
	laserOverheat: false,
	laserRange: null,
	missilesPerLife: null,
	spawnPoints: null,
	scoreLimit: null,
	planetGrowth: null,
	powerUps: false,
});

/** A mutable deep copy (presets are frozen). */
export function cloneRules(rules: Readonly<MatchRules>): MatchRules {
	return { ...rules, powerUpTypes: { ...rules.powerUpTypes } };
}

function clampToSpec(value: number, spec: NumberSpec): number {
	const clamped = Math.min(spec.max, Math.max(spec.min, value));
	const stepped = Math.round(clamped / spec.step) * spec.step;
	// Strip float noise from the rounding (0.1 steps) and stay inside the range.
	return Math.min(spec.max, Math.max(spec.min, Number(stepped.toFixed(6))));
}

/**
 * Validates untrusted rules (saved settings, URL, network) into a complete MatchRules:
 * missing or malformed fields take `fallback`'s value, numbers are clamped and stepped,
 * and the spawn interval keeps min ≤ max. `maxLaserRange` clamps the range per map.
 */
export function normalizeRules(
	raw: unknown,
	fallback: Readonly<MatchRules> = MODERN_RULES,
	maxLaserRange = LASER_RANGE_LIMIT,
): MatchRules {
	const r = typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
	const out = cloneRules(fallback);
	for (const key of BOOLEAN_RULE_KEYS) {
		const v = r[key];
		if (typeof v === "boolean") out[key] = v;
	}
	for (const key of NUMBER_RULE_KEYS) {
		const spec = RULE_SPECS[key];
		const v = r[key];
		if (v === null && spec.nullable) out[key] = null as never;
		else if (typeof v === "number" && Number.isFinite(v)) out[key] = clampToSpec(v, spec) as never;
		else {
			const f = out[key];
			out[key] = (f === null ? (spec.nullable ? null : spec.min) : clampToSpec(f, spec)) as never;
		}
	}
	const types = r.powerUpTypes;
	if (typeof types === "object" && types !== null) {
		for (const kind of POWER_UP_KINDS) {
			const v = (types as Record<string, unknown>)[kind];
			if (typeof v === "boolean") out.powerUpTypes[kind] = v;
		}
	}
	if (out.powerUpIntervalMaxTicks < out.powerUpIntervalMinTicks) {
		out.powerUpIntervalMaxTicks = out.powerUpIntervalMinTicks;
	}
	if (out.laserRange !== null) out.laserRange = Math.min(out.laserRange, maxLaserRange);
	return out;
}

/** Deep equality of two rule sets. */
export function sameRules(a: Readonly<MatchRules>, b: Readonly<MatchRules>): boolean {
	return (
		BOOLEAN_RULE_KEYS.every((k) => a[k] === b[k]) &&
		NUMBER_RULE_KEYS.every((k) => a[k] === b[k]) &&
		POWER_UP_KINDS.every((k) => a.powerUpTypes[k] === b.powerUpTypes[k])
	);
}

/** Duration of a timed power-up under these rules. */
export function powerUpDuration(rules: MatchRules, kind: "tripleLaser" | "warp"): number {
	const own = kind === "tripleLaser" ? rules.tripleLaserDurationTicks : rules.warpDurationTicks;
	return own ?? rules.powerUpDurationTicks;
}
